/**
 * Created by Marcus Zhao on 8/24/2021.
 * Description:
 *
 * ------ maintenance history ------
 * Updated by Marcus 4/7/2022 Support search address by tamale and google. and set address as a formatting.
 * Updated by Daniel Wang 8/5/2022 Handle some special country cases
 **/
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { DefaultCountryList, SpecialCountryList } from '@tamalelibs/constants/configuration.country.constants';
import { DefaultStateList } from '@tamalelibs/constants/configurationState.constants';
import { DefaultWordCites } from '@tamalelibs/constants/DefaultWorldCities.constants';
import { Contact } from '@tamalelibs/models/contact.model';
import { AddressDetail } from '@tamalelibs/models/job-function';
import { EntityService } from '@tamalelibs/services/entity.service';
import { MapConfig } from '../../tam-dashboard/tam-dashboard-map-tile/tam-dashboard-map/tam-dashboard-map.model';
import { EntityDialogOpenOptions } from '../entity-dialog.model';
import { EntityDialogType } from './../entity-dialog.model';

@Component({
    selector: 'tam-entity-address',
    templateUrl: './entity-address.component.html',
    styleUrls: ['./entity-address.component.scss'],
    standalone: false
})
export class EntityAddressComponent implements OnInit {
    @Input() address: AddressDetail;
    @Input() dialogOpenOptions: EntityDialogOpenOptions;
    @Output() addressChange = new EventEmitter();
    connector = ' ';
    isCustom = false;
    isShowSearchAddress = false;
    mapConfig = new MapConfig();
    valueCountryList = [];
    valueStateList = [];

    private _valueStateList = [];
    private _valueCountryList = [];

    constructor(private _entityService: EntityService,
    ) { }

    ngOnInit() {
        this._initData();
    }

    /**
     * Change Country Value and change State List
     *
     * @param {*} item
     * @memberof EntityAddressComponent
     */
    changeValueCountry(item) {
        this._setStateData(item);
        this._changeAddress();
    }

    /**
     * Change Street
     *
     * @param {*} item
     * @memberof EntityAddressComponent
     */
    changeValueStreet(item) {
        this._changeAddress();
    }

    /**
    * Change Street2
    *
    * @param {*} item
    * @memberof EntityAddressComponent
    */
    changeValueStreet2(item) {
        this._changeAddress();
    }

    /**
     * Change Country
     *
     * @param {*} item
     * @memberof EntityAddressComponent
     */
    changeValueCity(item) {
        this._changeAddress();
    }

    /**
     * Change State
     *
     * @param {*} item
     * @memberof EntityAddressComponent
     */
    changeValueState(item) {
        this._changeAddress();
    }

    /**
      * zip change
      *
      * @param {*} item
      * @memberof ContactAddressComponent
      */
    changeValueZip(item) {
        this._changeAddress();
    }

    /**
     *
     * click search address
     * @memberof EntityAddressComponent
     */
    clickSearchAddress() {
        this.isShowSearchAddress = !this.isShowSearchAddress;
        if (this.dialogOpenOptions.openType === EntityDialogType.EditEntity) {

            let address = '';
            if (this.address.street2) {
                address = `${this.address.street2}`;
            }
            if (this.address.street) {
                address = `${address}${this.connector}${this.address.street}`;
            }
            if (this.address.city) {
                address = `${address}${this.connector}${this.address.city}`;
            }
            if (this.address.state) {
                address = `${address}${this.connector}${this.address.state}`;
            }
            if (this.address.zip) {
                address = `${address}${this.connector}${this.address.zip}`;
            }
            if (this.address.country) {
                address = `${address}${this.connector}${this.address.country}`;
            }
            this.mapConfig.inputValue = address.trim();
        } else {
            this.mapConfig.inputValue = this.address.street;
        }
    }

    /**
     * filter state
     *
     * @param {*} value
     * @memberof EntityAddressComponent
     */
    filterChange(value) {
        if (!value) {
            this.valueStateList = this._valueStateList;
        } else {
            this.valueStateList = this._valueStateList.filter((s) => s && s.toLowerCase().indexOf(value.toLowerCase()) !== -1);
        }
        this.isCustom = false;
    }

    /**
     * filter country
     *
     * @param {*} value
     * @memberof EntityAddressComponent
     */
    filterChangeCountry(value) {
        if (!value) {
            this.valueCountryList = this._valueCountryList;
        } else {
            this.valueCountryList = this._valueCountryList.filter((s) => s && s.toLowerCase().indexOf(value.toLowerCase()) !== -1);
        }
        this.isCustom = false;
    }

    /**
     * set address to address control
     * include tamale address and google address.
     *
     * @param {*} value
     * @memberof EntityAddressComponent
     */
    setSelectedAddress(value) {
        if (value.payload && value.payload.mapContactModel) {
            if (value.payload.mapContactModel.contact) {
                const contact: Contact = value.payload.mapContactModel.contact;
                const addressValueType = value.payload.mapContactModel.selectedAddressType;
                // get select address from webAddress list
                const entityIndex = value.payload.mapContactModel.entityIndex;
                if (entityIndex > -1) {
                    this.address.country = contact.webAddress[entityIndex].country;
                    this._processCountry();
                    this._setStateData(this.address.country);
                    this.address.city = contact.webAddress[entityIndex].city;
                    this.address.state = contact.webAddress[entityIndex].state;
                    this._processState();
                    this.address.street = contact.webAddress[entityIndex].street;
                    this.address.street2 = contact.webAddress[entityIndex].street2;
                    this.address.zip = contact.webAddress[entityIndex].zip;
                }
            } else {
                const googleAddress = value.payload.mapContactModel.selectedAddress;
                this._entityService.getAddress(googleAddress).pipe(
                    take(1),
                ).subscribe(contact => {
                    this.address.country = contact.country;
                    this._processCountry();
                    this._setStateData(this.address.country);
                    this.address.state = contact.state;
                    this._processState();
                    this.address.city = contact.city;
                    this.address.street = contact.street;
                    this.address.street2 = contact.street2;
                    this.address.zip = contact.zip;
                },
                    error => {
                        this.address.street = googleAddress;
                    }
                );
            }
        }
        this.isShowSearchAddress = false;
    }

    /**
     * Output address
     *
     * @private
     * @memberof EntityAddressComponent
     */
    private _changeAddress() {
        this.addressChange.emit(this.address);
    }

    /**
      *init DropDown value
      *
      * @private
      * @memberof ContactAddressComponent
      */
    private _initData() {
        this.mapConfig.isFromMap = false;
        this.valueCountryList = Array.from(new Set(DefaultWordCites.map(data => data.country).sort()));
        this._valueCountryList = this.valueCountryList;
        this._processCountry();
        this._setStateData(this.address.country);
        this._processState();
    }

    /**
     * set Country data by RC created
     *
     * @private
     * @memberof EntityAddressComponent
     */
    private _processCountry() {
        this.isCustom = true;
        const _country = this.address.country;
        if (_country) {
            // handle some special cases.
            const specialCountry = SpecialCountryList.find(item => item.name.toLowerCase() === _country.toLowerCase());
            if (specialCountry) {
                this.address.country = specialCountry.country;
            } else {
                const countrySize = _country.length;
                if (countrySize > 0) {
                    let index = -1;
                    switch (countrySize) {
                        case 2:
                            index = DefaultCountryList.findIndex(data => data.a2 === _country.toUpperCase());
                            break;
                        case 3:
                            index = DefaultCountryList.findIndex(data => data.a3 === _country.toUpperCase());
                            break;
                        default:
                            index = this.valueCountryList.findIndex(data => data.toLowerCase() === _country.toLowerCase());
                            break;
                    }
                    if (index !== -1 && countrySize < 4) {
                        const normalCountry = DefaultCountryList[index].country;
                        this.address.country = normalCountry;
                    } else if (index !== -1 && countrySize >= 4) {
                        this.address.country = _country;
                    } else {
                        if (this.address.street !== '') {
                            this.address.country = _country;
                        } else {
                            this.address.country = '';
                        }
                    }
                }
            }
        }
    }

    /**
     * set State by RC created
     *
     * @private
     * @memberof EntityAddressComponent
     */
    private _processState() {
        // due to old state include &nbsp not space .like 'New York' not equals 'New York'
        // convert to space.
        if (this.address.state) {
            const _state = decodeURI(encodeURI(this.address.state).replace(/%C2%A0/g, '%20'));
            if (_state) {
                if (_state.length === 2) {
                    const index = DefaultStateList.findIndex(data => data.a2 === _state.toUpperCase());
                    if (index !== -1) {
                        const normalState = DefaultStateList[index].state;
                        // state dropdown must include normalState.
                        const selectItemIndex = this.valueStateList.findIndex(item => item.toLowerCase() === normalState.toLowerCase());
                        if (selectItemIndex > -1) {
                            this.address.state = this.valueStateList[selectItemIndex];
                        }
                    } else {
                        if ((this.address && this.address.street && this.address.street !== '') || this.dialogOpenOptions.openType === EntityDialogType.NewEntity) {
                            this.address.state = _state;
                        } else {
                            this.address.state = '';
                        }
                    }
                } else {
                    const index = this.valueStateList.findIndex(data => data.toLowerCase() === _state.toLowerCase());
                    if (index !== -1) {
                        this.address.state = _state;
                    } else {
                        // support for search address in new entity mode
                        if ((this.address && this.address.street && this.address.street !== '') || this.dialogOpenOptions.openType === EntityDialogType.NewEntity) {
                            this.address.state = _state;
                        } else {
                            this.address.state = '';
                        }
                    }
                }
            }
        }
    }

    /**
     * set State data by country change
     * @private
     * @param {*} item
     * @memberof ContactAddressComponent
     */
    private _setStateData(item) {
        const cityList = DefaultWordCites.filter(data => data.country === item);
        this.valueStateList = Array.from(new Set(cityList.map(data => data.subcountry).sort()));
        this.valueStateList.splice(0, 0, '');
        this._valueStateList = this.valueStateList;
    }

}

